import "yet-another-react-lightbox/styles.css";
import "yet-another-react-lightbox/plugins/thumbnails.css";
import "yet-another-react-lightbox/plugins/counter.css";
import Lightbox from "yet-another-react-lightbox";
import { Thumbnails, Counter, Zoom } from "yet-another-react-lightbox/plugins";
import {
  MdClose,
  MdOutlineSaveAlt,
  MdPictureAsPdf,
  MdOutlineNavigateBefore,
  MdOutlineNavigateNext,
} from "react-icons/md";
import clsx from "clsx";
import LoadingWithText from "src/components/LoadingWithText/LoadingWithText";
import TransactionPdfSlide from "./TransactionPdfSlide";
import React, { useMemo } from "react";
import useMobile from "src/hooks/useMobile";
import useCollapsibles from "src/hooks/useCollapsibles";
import SmartImage from "src/components/SmartImage/SmartImage";
import { ICON_SIZES } from "src/constants/icons";

interface TransactionAttachmentsLightboxProps {
  slides: any[];
  index: number;
  onClose: () => void;
  setIndex: (i: number) => void;
  thumbnailsRef: any;
  unifiedAttachmentsList: any[];
}

const TransactionAttachmentsLightbox: React.FC<
  TransactionAttachmentsLightboxProps
> = ({
  slides,
  index,
  onClose,
  setIndex,
  unifiedAttachmentsList,
  thumbnailsRef,
}) => {
  const { isMobile } = useMobile();
  const { updateCollapsible, collapsiblePreferences } = useCollapsibles();

  const hasMultipleSlides = useMemo(() => {
    return slides?.length > 1;
  }, [slides]);

  return (
    <Lightbox
      on={{
        view: ({ index }) => {
          setIndex(index);
        },
        exiting: () => {
          updateCollapsible({
            lightboxThumbnailsOpen: !!thumbnailsRef.current?.visible,
          });
        },
      }}
      slides={slides}
      plugins={[Thumbnails, Counter, Zoom]}
      toolbar={{
        buttons: ["close"],
      }}
      index={index}
      open
      close={onClose}
      render={{
        iconClose: () => <MdClose size={ICON_SIZES.SM} />,
        iconPrev: () =>
          hasMultipleSlides ? (
            <MdOutlineNavigateBefore
              size={!isMobile ? ICON_SIZES.XL : ICON_SIZES.MD_LG}
            />
          ) : null,
        iconNext: () =>
          hasMultipleSlides ? (
            <MdOutlineNavigateNext
              size={!isMobile ? ICON_SIZES.XL : ICON_SIZES.MD_LG}
            />
          ) : null,
        iconLoading: () => <LoadingWithText />,
        iconDownload: () => <MdOutlineSaveAlt size={ICON_SIZES.SM} />,
        slide: ({ slide }) => {
          return slide?.type === undefined ? (
            <TransactionPdfSlide url={slide?.src} />
          ) : undefined;
        },
        buttonZoom: () => null,
        thumbnail: ({ slide }) => {
          if (slide.type === undefined) {
            return (
              <div
                className={clsx([
                  "flex",
                  "justify-center",
                  "items-center",
                  "w-full",
                  "bg-white",
                  "dark:bg-black",
                  "h-full",
                ])}
              >
                <MdPictureAsPdf
                  size={ICON_SIZES.SM}
                  className={clsx(["text-blue-800", "dark:text-purple-400"])}
                />
              </div>
            );
          }

          // fallback a imagen por default
          return (
            <SmartImage
              src={(slide as any).src}
              alt="Imagen"
              className={clsx(["object-cover", "rounded", "h-full", "w-full"])}
            />
          );
        },
        // ...(isMobile ? { buttonNext: () => null } : {}),
        // ...(isMobile ? { buttonPrev: () => null } : {}),
      }}
      thumbnails={{
        gap: 6,
        borderRadius: 6,
        border: 0,
        borderColor: "#cccccc",
        imageFit: "cover",
        showToggle: true,
        padding: 0,
        hidden: !collapsiblePreferences?.lightboxThumbnailsOpen,
        ref: thumbnailsRef,
        position: isMobile ? "bottom" : "bottom",
        vignette: false,
      }}
      carousel={{ finite: !hasMultipleSlides }}
      styles={{
        root: {
          "--yarl__color_backdrop": "rgba(0,0,0,0.9)",
        },
      }}
      controller={{
        closeOnPullUp: slides?.[index]?.type !== undefined,
        closeOnPullDown: slides?.[index]?.type !== undefined,
      }}
    />
  );
};

export default React.memo(TransactionAttachmentsLightbox);
